import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { action, makeObservable, observable } from "mobx";
import PipelineFault from "models/PipelineFault";
import type ProcessFailure from "models/ProcessFailure";
import { AuthStore } from "stores/AuthStore";
import { BaseStore } from "stores/BaseStore";
import { DatePickerComponentStore } from "stores/ComponentStores/DatePickerComponentStore";
import { LayoutDrawerStore } from "stores/Layout/LayoutDrawerStore";
import * as RootStore from "stores/RootStore";
import { PipelineService } from "../../../../../services/PipelineService";

@RootStore.AcxStore
export default class RequeueStore extends BaseStore {
    readonly authStore: AuthStore;
    readonly pipelineService: PipelineService;

    public readonly datePickerStore = new DatePickerComponentStore();

    @observable
    public selectedFailure?: ProcessFailure = undefined;
    public failures: ProcessFailure[];
    readonly requeueDgStore: AcxDataGridStore;

    @observable
    public faults?: PipelineFault[] = undefined;
    readonly faultDgStore: AcxDataGridStore;
    readonly drawerStore: LayoutDrawerStore;

    @observable
    public requeueDialogOpen: boolean = false;

    @observable
    public requeueToBulkLane: boolean = false;

    constructor(public rootStore: RootStore.IRootStore) {
        super("RequeueDataGridStore");
        makeObservable(this);

        this.requeueDgStore = new AcxDataGridStore("Requeue", "Administrative");
        this.faultDgStore = new AcxDataGridStore(
            "RequeueFaults",
            "Administrative",
        );
        this.pipelineService = new PipelineService();
        this.authStore = rootStore.getStore(AuthStore);
        this.drawerStore = this.rootStore.getStore(LayoutDrawerStore);
    }

    public async updateFailures() {
        this.requeueDgStore.rows = [];
        this.requeueDgStore.clearSelected();
        this.requeueDgStore.isLoading = true;
        try {
            this.failures = await this.pipelineService.getFailuresByOrg(
                this.authStore.orgStore.selectedOrganization?.id!,
                this.datePickerStore.beginDate,
                this.datePickerStore.endDate,
            );
            this.requeueDgStore.rows = this.failures;
        } catch (error) {
            throw error;
        } finally {
            this.requeueDgStore.isLoading = false;
        }
    }

    @action
    public openRequeueDialog() {
        this.requeueToBulkLane = false;
        this.requeueDialogOpen = true;
    }

    @action
    public closeRequeueDialog() {
        this.requeueDialogOpen = false;
    }

    @action
    public setRequeueToBulkLane(value: boolean) {
        this.requeueToBulkLane = value;
    }

    /**
     * Sends the selected failures to the requeuing dispatcher to be requeued immediately.
     * Returns the number of conversations requeued.
     */
    public async requeueFailures(): Promise<number> {
        this.setTaskLoading("Requeue", true);

        const amdIdsToRequeue = this.requeueDgStore.selectedRows.map(
            (item) => (item as ProcessFailure).id,
        );

        try {
            // post throws a ServiceError on any non-2xx response
            await this.pipelineService.requeueAMDs(
                amdIdsToRequeue,
                this.requeueToBulkLane,
            );
            this.failures = this.failures.filter(
                (failure) => !amdIdsToRequeue.includes(failure.id),
            );
            this.requeueDgStore.rows = this.failures;
            // Only reset on success so a failed requeue can be retried as-is
            this.requeueDgStore.clearSelected();
            this.closeRequeueDialog();
            return amdIdsToRequeue.length;
        } finally {
            this.setTaskLoading("Requeue", false);
        }
    }

    public async updateFaults(failure: ProcessFailure) {
        if (
            !this.faults ||
            !this.faults.length ||
            this.faults.find((fault) => fault.correlationId !== failure.id)
        ) {
            try {
                this.faults =
                    await this.pipelineService.getFaultsByOrgandFailure(
                        this.authStore.orgStore.selectedOrganization?.id!,
                        failure,
                    );
                this.faultDgStore.rows = this.faults!;
            } catch (error) {
                throw error;
            }
        }
    }
}
